import React, { useEffect, useState } from 'react';
import { CalendarDays, ExternalLink } from 'lucide-react';
import { apiCall } from '../_shared';
import { RatesCalendar } from './RatesCalendar';
import { ExtranetPanel, OpenClosePanel, RatePlansPanel } from './RatesPanels';
import { RatePlans } from './RatePlans';
import { ValueAdds } from './ValueAdds';
import { ChildRates } from './ChildRates';
import { Promotions } from './Promotions';
import { RateSyncPanel } from './RateSyncPanel';
import { BookingPageTexts } from './BookingPageTexts';
import { TourOperators } from './TourOperators';
import { extranetUrl } from './useSetting';
import { RateEditorCard } from '../stats/RateEditorCard';
import { RateHistoryCard } from '../stats/RateHistoryCard';
import { ChannelHealthBadge } from '../ChannelHealthBadge';

// Same menu as Booking.com → Rates & availability. Everything is live here; extranet-only layers also deep-link out.
const MENU = [
  { id: 'calendar', label: 'Calendar', live: true },
  { id: 'openclose', label: 'Open/close rooms', live: true },
  { id: 'copy', label: 'Copy rates to future dates', live: true },
  { id: 'bulk', label: 'Bulk edit & per-person prices', live: true },
  { id: 'plans', label: 'Rate plans', live: true },
  { id: 'value_adds', label: 'Value adds', live: true },
  { id: 'connectivity', label: 'Connectivity errors', live: true },
  { id: 'country_rates', label: 'Country rates' },
  { id: 'mobile_rates', label: 'Mobile rates' },
  { id: 'child_rates', label: 'Child rates', live: true },
  { id: 'promotions', label: 'Promotions', live: true },
  { id: 'rate_sync', label: 'Rate sync check', live: true },
  { id: 'booking_page', label: 'Booking page texts', live: true },
  { id: 'tour_operators', label: 'Tour operator price list', live: true },
];

const Connectivity = ({ userRole }) => (
  <div className="space-y-4">
    <div className="bg-white rounded-xl border p-4" data-testid="rates-connectivity">
      <h3 className="text-sm font-bold text-slate-900">Connectivity</h3>
      <p className="text-xs text-slate-600 mt-1">Beds24 ↔ Booking.com link health as seen from here, plus every rate/availability push and its result. Failed pushes are flagged red in the history and can be re-sent.</p>
      <div className="mt-3"><ChannelHealthBadge userRole={userRole} /></div>
    </div>
    <RateHistoryCard refreshKey={0} />
    <ExtranetPanel id="connectivity" />
  </div>
);

// Booking.com's red "Closed/Not bookable" strip — driven by our channel-health check.
const PropertyBanner = () => {
  const [h, setH] = useState(null);
  useEffect(() => { apiCall('/api/admin/rates/channel-health').then(setH).catch(() => {}); }, []);
  const issue = (h?.issues || []).find((i) => i.level === 'red') || (h?.issues || [])[0];
  if (!issue) return null;
  const red = issue.level === 'red';
  return (
    <div className={`rounded-lg border px-4 py-3 flex flex-wrap items-center justify-center gap-3 text-xs ${red ? 'bg-red-50 border-red-300 text-red-900' : 'bg-amber-50 border-amber-300 text-amber-900'}`} data-testid="ra-property-banner">
      <span><b>{red ? 'Closed/Not bookable:' : 'Attention:'}</b> {issue.title} — {issue.detail}</span>
      <a href={extranetUrl('home')} target="_blank" rel="noreferrer" className="bg-[#0071c2] text-white rounded px-3 py-1.5 font-semibold inline-flex items-center gap-1" data-testid="ra-banner-action"><ExternalLink className="w-3 h-3" /> How can I reopen my property?</a>
    </div>
  );
};

const RatesAvailability = ({ userRole }) => {
  const [item, setItem] = useState('calendar');
  const [pushKey, setPushKey] = useState(0);
  const menu = MENU.map((m) => (
    <button key={m.id} type="button" onClick={() => setItem(m.id)} className={`w-full text-left px-3 py-2 rounded-lg text-xs flex items-center justify-between ${item === m.id ? 'bg-[#003b95] text-white font-semibold' : 'hover:bg-slate-100 text-slate-700'}`} data-testid={`ra-menu-${m.id}`}>
      <span>{m.label}</span>{!m.live && <span className={`text-[9px] uppercase ${item === m.id ? 'text-white/70' : 'text-slate-400'}`}>extranet</span>}
    </button>
  ));
  let body = null;
  if (item === 'calendar') body = <RatesCalendar onBulk={() => setItem('bulk')} />;
  else if (item === 'openclose') body = <OpenClosePanel />;
  else if (item === 'copy' || item === 'bulk') body = <RateEditorCard initialMode={item === 'copy' ? 'copy' : 'price'} onPushed={() => setPushKey((k) => k + 1)} />;
  else if (item === 'plans') body = <RatePlans standardRates={<RatePlansPanel compact />} />;
  else if (item === 'value_adds') body = <ValueAdds />;
  else if (item === 'child_rates') body = <ChildRates />;
  else if (item === 'promotions') body = <Promotions />;
  else if (item === 'rate_sync') body = <RateSyncPanel />;
  else if (item === 'booking_page') body = <BookingPageTexts />;
  else if (item === 'tour_operators') body = <TourOperators />;
  else if (item === 'connectivity') body = <Connectivity userRole={userRole} />;
  else body = <ExtranetPanel id={item} />;
  const withHistory = item === 'openclose' || item === 'copy' || item === 'bulk';
  return (
    <div data-testid="rates-availability" className="space-y-3">
      <h2 className="text-base md:text-lg font-bold text-slate-900 flex items-center gap-2"><CalendarDays className="w-5 h-5 text-[#003b95]" /> Rates & availability</h2>
      <PropertyBanner />
      <div className="grid lg:grid-cols-[220px_1fr] gap-4">
        <nav className="bg-white rounded-xl border p-2 space-y-0.5 h-fit" data-testid="ra-menu">{menu}</nav>
        <div className="space-y-4 min-w-0">{body}{withHistory && <RateHistoryCard refreshKey={pushKey} />}</div>
      </div>
    </div>
  );
};

export default RatesAvailability;
